// Loads the Google Maps JavaScript SDK on demand (once) and exposes address
// geocoding. Everything is off when REACT_APP_GOOGLE_MAPS_API_KEY is unset, so
// callers can render nothing and the forms behave exactly as before.
//
// No @types/google.maps dependency: the SDK surface we touch is tiny, so it is
// typed as `any` at this boundary and the rest of the app sees LatLng only.

const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY?.trim();

export const isGoogleMapsConfigured = Boolean(API_KEY);

export interface LatLng {
  lat: number;
  lng: number;
}

let loadPromise: Promise<any> | null = null;

export function loadGoogleMaps(): Promise<any> {
  const existing = (window as any).google?.maps;
  if (existing) return Promise.resolve(existing);
  if (!API_KEY) {
    return Promise.reject(new Error("REACT_APP_GOOGLE_MAPS_API_KEY is not set"));
  }

  if (!loadPromise) {
    loadPromise = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(API_KEY)}&v=weekly`;
      script.async = true;
      script.onload = () => {
        const maps = (window as any).google?.maps;
        if (maps) resolve(maps);
        else {
          loadPromise = null;
          reject(new Error("Google Maps SDK loaded without a maps object"));
        }
      };
      script.onerror = () => {
        // Allow a retry on the next call (e.g. transient network failure).
        loadPromise = null;
        script.remove();
        reject(new Error("Failed to load the Google Maps SDK"));
      };
      document.head.appendChild(script);
    });
  }
  return loadPromise;
}

/**
 * Resolve a free-text address to coordinates. Returns null when Google can't
 * match it (the SDK rejects with ZERO_RESULTS) — callers fall back to letting
 * the user place the pin by hand. Rethrows only SDK-load failures.
 */
export async function geocodeAddress(address: string): Promise<LatLng | null> {
  const query = address.trim();
  if (!query) return null;

  const maps = await loadGoogleMaps();
  try {
    // `region` biases ambiguous results toward India, the deployment market.
    const { results } = await new maps.Geocoder().geocode({
      address: query,
      region: "in",
    });
    const location = results?.[0]?.geometry?.location;
    return location ? { lat: location.lat(), lng: location.lng() } : null;
  } catch {
    return null;
  }
}
